import {
  Checkbox,
  LinkedinSquare,
  Mail,
  Signal,
  Whatsapp,
} from '@saleshandy/icons';
import moment from 'moment';
import { getFormattedDateWithMoment } from '../../../../../../../../../shared/utils';
import {
  SequenceStepType,
  StepType,
} from '../../../../../../../../sequence/enums';
import { getTaskContentLabel } from '../../../../../../../../tasks/components/tasker-modal/utils/helper';
import {
  isCallTask,
  isWhatsappTask,
} from '../../../../../../../../tasks/components/tasks-content/utils/helper';
import {
  TaskFilterDisplayLabel,
  TaskFilterKey,
  TaskStatusDisplayLabel,
  TaskStatusKey,
} from './enums';
import { TaskAction } from '../../../../../../../../tasks/utils/enums';

/**
 * Maps TaskFilterKey to taskType array for API calls
 * @param filterKey - The selected filter key
 * @returns Comma-separated string of taskType IDs or undefined for "All"
 */
export const getTaskTypeFromFilter = (
  filterKey: TaskFilterKey,
): string | undefined => {
  switch (filterKey) {
    case TaskFilterKey.Call:
      return '11,12,13,14,15';
    case TaskFilterKey.LinkedInAutomation:
      return '19,20';
    case TaskFilterKey.Linkedin:
      return '2,3,4,5,6';
    case TaskFilterKey.Whatsapp:
      return '16,17,18';
    case TaskFilterKey.ManualTasks:
      return '9';
    case TaskFilterKey.All:
    default:
      return undefined;
  }
};

export const getTaskFilterArray = () => [
  {
    label: TaskFilterDisplayLabel.All,
    key: TaskFilterKey.All,
  },
  {
    label: TaskFilterDisplayLabel.Call,
    key: TaskFilterKey.Call,
  },
  {
    label: TaskFilterDisplayLabel.LinkedInAutomation,
    key: TaskFilterKey.LinkedInAutomation,
  },
  {
    label: TaskFilterDisplayLabel.Linkedin,
    key: TaskFilterKey.Linkedin,
  },
  {
    label: TaskFilterDisplayLabel.Whatsapp,
    key: TaskFilterKey.Whatsapp,
  },
  {
    label: TaskFilterDisplayLabel.ManualTasks,
    key: TaskFilterKey.ManualTasks,
  },
];

export const getTaskStatusArray = () => [
  {
    label: TaskStatusDisplayLabel.All,
    key: TaskStatusKey.All,
  },
  {
    label: TaskStatusDisplayLabel.DueToday,
    key: TaskStatusKey.DueToday,
  },
  {
    label: TaskStatusDisplayLabel.Upcoming,
    key: TaskStatusKey.Upcoming,
  },
  {
    label: TaskStatusDisplayLabel.Overdue,
    key: TaskStatusKey.Overdue,
  },
  {
    label: TaskStatusDisplayLabel.Completed,
    key: TaskStatusKey.Completed,
  },
  {
    label: TaskStatusDisplayLabel.Skipped,
    key: TaskStatusKey.Skipped,
  },
];

export const resolveTaskTypeKey = (type?: string | SequenceStepType) => {
  if (typeof type === 'number') {
    if (
      type === SequenceStepType.AimfoxCampaignPush ||
      type === SequenceStepType.HeyReachCampaignPush
    ) {
      return 'linkedin-automation';
    }

    if (
      type === SequenceStepType.LinkedInConnectionRequest ||
      type === SequenceStepType.LinkedInMessage ||
      type === SequenceStepType.LinkedInInMail ||
      type === SequenceStepType.LinkedInViewProfile ||
      type === SequenceStepType.LinkedInPostInteraction
    ) {
      return 'linkedin';
    }

    if (isCallTask(type)) {
      return 'call';
    }

    if (isWhatsappTask(type)) {
      return 'whatsapp';
    }

    return 'task';
  }

  return type?.toLowerCase() || '';
};

export const resolveSequenceStepType = (
  type?: string | SequenceStepType,
): SequenceStepType | undefined => {
  if (typeof type === 'number') {
    return type;
  }

  const normalizedType = type?.toLowerCase() || '';

  if (normalizedType === 'email') {
    return SequenceStepType.Email;
  }

  if (normalizedType === 'linkedin') {
    return SequenceStepType.LinkedInMessage;
  }

  if (normalizedType === 'call') {
    return SequenceStepType.Call;
  }

  if (normalizedType === 'task' || normalizedType === 'checkbox') {
    return SequenceStepType.Custom;
  }

  if (normalizedType === 'whatsapp') {
    return SequenceStepType.WhatsAppMessage;
  }

  return undefined;
};

export const getPriorityClassName = (priority?: string) => {
  if (!priority) return '';
  const normalizedPriority = priority.toLowerCase();
  if (['urgent', 'high', 'normal', 'low'].includes(normalizedPriority)) {
    return normalizedPriority;
  }
  // Map common variations
  if (normalizedPriority.includes('urgent')) return 'urgent';
  if (normalizedPriority.includes('high')) return 'high';
  if (normalizedPriority.includes('normal')) return 'normal';
  if (normalizedPriority.includes('low')) return 'low';
  return 'normal'; // default
};

export const getDateColumnCellValue = (
  tStatus: TaskStatusKey,
  date: string,
) => {
  if (tStatus === TaskStatusKey.DueToday) {
    return 'Due Today';
  }
  if (tStatus === TaskStatusKey.Upcoming) {
    return `Due in ${getFormattedDateWithMoment(date)}`;
  }
  if (tStatus === TaskStatusKey.Overdue) {
    return `Due ${getFormattedDateWithMoment(date)} ago`;
  }
  return `${getFormattedDateWithMoment(date)} ${
    getFormattedDateWithMoment(date).toLowerCase() !== 'today' ? 'ago' : ''
  }`;
};

export const getTaskSubjectLabel = (taskType?: string | SequenceStepType) => {
  const baseLabel = getTaskContentLabel(taskType as SequenceStepType);
  if (!baseLabel) return 'Subject';
  return `${baseLabel} Subject`;
};

export const getTaskMessageLabel = (taskType?: string | SequenceStepType) => {
  const baseLabel = getTaskContentLabel(taskType as SequenceStepType);
  if (!baseLabel) return 'Message';
  if (taskType === SequenceStepType.WhatsAppVoiceCall) {
    return `${baseLabel} Script`;
  }
  return `${baseLabel} Message`;
};

const taskTypeIcons = [
  {
    type: 'call',
    icon: Signal,
  },
  {
    type: 'linkedin-automation',
    icon: LinkedinSquare,
  },
  {
    type: 'linkedin',
    icon: LinkedinSquare,
  },
  {
    type: 'whatsapp',
    icon: Whatsapp,
  },
  {
    type: 'checkbox',
    icon: Checkbox,
  },
  {
    type: 'task',
    icon: Checkbox,
  },
  {
    type: 'email',
    icon: Mail,
  },
];

export const getTaskTypeIcon = (type?: string | SequenceStepType) => {
  const normalizedType = resolveTaskTypeKey(type);
  const iconConfig = taskTypeIcons.find((item) => item.type === normalizedType);
  return iconConfig?.icon;
};

export const getTaskContainerClass = (
  type?: string | SequenceStepType,
): string => {
  const normalizedType = resolveTaskTypeKey(type);
  switch (normalizedType) {
    case 'linkedin-automation':
    case 'linkedin':
      return 'upcoming-task-linkedln';
    case 'call':
      return 'upcoming-task-introduction-call';
    case 'whatsapp':
      return 'upcoming-task-whatsapp';
    case 'task':
    case 'checkbox':
    case 'email':
    default:
      return 'upcoming-task-column';
  }
};

export const getTaskIconClass = (type?: string | SequenceStepType): string => {
  const normalizedType = resolveTaskTypeKey(type);
  switch (normalizedType) {
    case 'linkedin-automation':
    case 'linkedin':
      return 'upcoming-task-linkedln-icon';
    case 'call':
      return 'upcoming-task-call-icon';
    case 'whatsapp':
      return 'upcoming-task-whatsapp-icon';
    case 'task':
    case 'checkbox':
    default:
      return 'task-checkbox-icon';
  }
};

export const getTaskHeaderClass = (
  type?: string | SequenceStepType,
): string => {
  const normalizedType = resolveTaskTypeKey(type);
  switch (normalizedType) {
    case 'linkedin-automation':
    case 'linkedin':
      return 'upcoming-task-linkedln-header';
    case 'call':
      return 'upcoming-task-introduction-call-header';
    case 'whatsapp':
    case 'task':
    case 'checkbox':
    case 'email':
    default:
      return 'upcoming-task-header';
  }
};

export const getTaskStatusFromDate = (dueAt?: string): TaskStatusKey => {
  if (!dueAt) return TaskStatusKey.Upcoming;

  const now = moment();
  const dueMoment = moment(dueAt);
  const today = moment().startOf('day');
  const dueDate = moment(dueAt).startOf('day');

  if (dueDate.isSame(today)) {
    return TaskStatusKey.DueToday;
  }

  if (now.isAfter(dueMoment)) {
    return TaskStatusKey.Overdue;
  }

  return TaskStatusKey.Upcoming;
};

export const getFormattedDueDate = (dueAt?: string): string => {
  if (!dueAt) return '';

  const taskStatus = getTaskStatusFromDate(dueAt);

  if (taskStatus === TaskStatusKey.DueToday) {
    return 'Due Today';
  }
  if (taskStatus === TaskStatusKey.Upcoming) {
    return `Due in ${getFormattedDateWithMoment(dueAt)}`;
  }
  if (taskStatus === TaskStatusKey.Overdue) {
    return `Due ${getFormattedDateWithMoment(dueAt)} ago`;
  }

  return `Due ${getFormattedDateWithMoment(dueAt)} ago`;
};

export const getFormattedSkippedDate = (skippedAt?: string): string => {
  if (!skippedAt) return '';
  const formattedDate = getFormattedDateWithMoment(skippedAt);
  return `Skipped ${formattedDate}${
    formattedDate.toLowerCase() !== 'today' ? ' ago' : ''
  }`;
};

export const getFormattedCompletedDate = (completedAt?: string): string => {
  if (!completedAt) return '';
  const formattedDate = getFormattedDateWithMoment(completedAt);
  return `Completed ${formattedDate}${
    formattedDate.toLowerCase() !== 'today' ? ' ago' : ''
  }`;
};

export const getFormattedFailedDate = (completedAt?: string): string => {
  if (!completedAt) return '';
  const formattedDate = getFormattedDateWithMoment(completedAt);
  return `Failed ${formattedDate}${
    formattedDate.toLowerCase() !== 'today' ? ' ago' : ''
  }`;
};

// Helper function to get initial step type and default step type based on filter
export const getInitialFormValues = (filter: TaskFilterKey) => {
  switch (filter) {
    case TaskFilterKey.All:
    case TaskFilterKey.Linkedin:
      return {
        defaultStepType: StepType.LinkedIn,
        initialStepType: SequenceStepType.LinkedInConnectionRequest,
      };
    case TaskFilterKey.Call:
      return {
        defaultStepType: StepType.Call,
        initialStepType: SequenceStepType.Introduction,
      };
    case TaskFilterKey.Whatsapp:
      return {
        defaultStepType: StepType.WhatsApp,
        initialStepType: SequenceStepType.WhatsAppMessage,
      };
    case TaskFilterKey.ManualTasks:
      return {
        defaultStepType: StepType.Task,
        initialStepType: SequenceStepType.Custom,
      };
    default:
      return {
        defaultStepType: StepType.LinkedIn,
        initialStepType: SequenceStepType.LinkedInConnectionRequest,
      };
  }
};

// Helper function to check if a date is today
export const isDateToday = (date: Date | string | null): boolean => {
  if (!date) return false;
  const dateMoment = moment(date);
  const today = moment().startOf('day');
  return dateMoment.startOf('day').isSame(today);
};

// Helper function to get current date in ISO format
export const getCurrentDateISO = (): string => moment().toISOString();

// Helper function to determine if task should be removed based on status and action
export const shouldRemoveTask = (
  status: TaskStatusKey,
  action: TaskAction,
  rescheduledDate?: Date | null,
): boolean => {
  // If status is "All", never remove (always update)
  if (status === TaskStatusKey.All) {
    return false;
  }

  // If status is "Overdue", always remove for any action
  if (status === TaskStatusKey.Overdue) {
    return true;
  }

  // If status is "Due Today"
  if (status === TaskStatusKey.DueToday) {
    if (action === TaskAction.ExecuteTask || action === TaskAction.SkipTask) {
      return true; // Remove on complete or skip
    }
    if (action === TaskAction.SnoozeTask) {
      // Remove if rescheduled date is not today
      return !isDateToday(rescheduledDate);
    }
  }

  // If status is "Upcoming"
  if (status === TaskStatusKey.Upcoming) {
    if (action === TaskAction.ExecuteTask || action === TaskAction.SkipTask) {
      return true; // Remove on complete or skip
    }
    if (action === TaskAction.SnoozeTask) {
      // Remove if rescheduled date is today (moves to Due Today)
      return isDateToday(rescheduledDate);
    }
  }

  // Default: remove for other statuses
  return true;
};

// Helper function to update task based on action
export const getUpdatedTask = (
  task: any,
  action: TaskAction,
  rescheduledDate?: Date | null,
): any => {
  const updatedTask = { ...task };

  if (action === TaskAction.ExecuteTask) {
    // Mark as completed with current date
    updatedTask.completedAt = getCurrentDateISO();
    updatedTask.skippedAt = undefined; // Clear skippedAt if it exists
  } else if (action === TaskAction.SkipTask) {
    // Mark as skipped with current date
    updatedTask.skippedAt = getCurrentDateISO();
    updatedTask.completedAt = undefined; // Clear completedAt if it exists
  } else if (action === TaskAction.SnoozeTask && rescheduledDate) {
    // Update dueAt with rescheduled date
    updatedTask.dueAt = moment(rescheduledDate).toISOString();
    // Clear completedAt and skippedAt if they exist
    updatedTask.completedAt = undefined;
    updatedTask.skippedAt = undefined;
  }

  return updatedTask;
};

// Helper function to calculate days difference between a date and today
export const getDaysDifference = (
  date: Date | string | null,
): number | null => {
  if (!date) return null;
  const dateMoment = moment(date).startOf('day');
  const today = moment().startOf('day');
  return dateMoment.diff(today, 'days');
};

// Helper function to determine if task should be added back on undo
export const shouldAddTaskBackOnUndo = (
  status: TaskStatusKey,
  originalAction: TaskAction,
  task: any,
  snoozeDate?: Date | null,
): boolean => {
  // If status is "All", don't add back (tasks were updated, not removed, so they're already visible)
  if (status === TaskStatusKey.All) {
    return false;
  }

  // If status is "Overdue", always add back
  if (status === TaskStatusKey.Overdue) {
    return true;
  }

  // For snooze actions, check the rescheduled date (what we snoozed TO)
  if (originalAction === TaskAction.SnoozeTask && snoozeDate) {
    const rescheduledDaysDiff = getDaysDifference(snoozeDate);

    // If status is "Due Today", don't add back if rescheduled date is today
    // (because task was updated, not removed, so it's already visible)
    if (status === TaskStatusKey.DueToday) {
      return rescheduledDaysDiff !== 0;
    }

    // If status is "Upcoming"
    if (status === TaskStatusKey.Upcoming) {
      // Issue 1: If rescheduled date is today (daysDiff === 0), add back
      // (task was removed because it moved to "Due Today", but on undo it goes back to "Upcoming")
      if (rescheduledDaysDiff === 0) {
        return true;
      }

      // Issue 2: If rescheduled date is still in "Upcoming" range (1+ days),
      // the task was updated (not removed), so don't add back
      // This covers cases like snoozing from "Due in 2 days" to "Due in 1 day"
      if (rescheduledDaysDiff !== null && rescheduledDaysDiff > 0) {
        return false;
      }
    }
  }

  // For complete or skip actions, check the task's original dueAt
  // (after undo, task goes back to original state)
  if (
    originalAction === TaskAction.ExecuteTask ||
    originalAction === TaskAction.SkipTask
  ) {
    const taskDueAt = task?.dueAt;
    if (!taskDueAt) return false;

    const daysDiff = getDaysDifference(taskDueAt);

    // If status is "Due Today", add back if original dueAt was today
    if (status === TaskStatusKey.DueToday) {
      return daysDiff === 0;
    }

    // If status is "Upcoming", add back if original dueAt was 1 or 2 days from today
    if (status === TaskStatusKey.Upcoming) {
      return daysDiff === 1 || daysDiff === 2;
    }
  }

  // Default: don't add back for other cases
  return false;
};

// Helper function to combine selected date with current time in local timezone
export const combineDateWithCurrentTime = (selectedDate: Date): Date => {
  const now = new Date();
  // Get the selected date components (year, month, day)
  const year = selectedDate.getFullYear();
  const month = selectedDate.getMonth();
  const day = selectedDate.getDate();

  // Get the current time components (hours, minutes, seconds, milliseconds)
  const hours = now.getHours();
  const minutes = now.getMinutes();
  const seconds = now.getSeconds();
  const milliseconds = now.getMilliseconds();

  // Create a new date with selected date + current time in local timezone
  return new Date(year, month, day, hours, minutes, seconds, milliseconds);
};
